import { useState, useEffect } from "react";

const TEAL = "#1B6B6B";
const TEAL_MID = "#2E8B8B";
const TEAL_PALE = "#E0F4F4";
const GOLD = "#E8C87A";
const WHITE = "#FFFFFF";
const DARK = "#0D2B2B";
const MID = "#2A4A4A";
const LIGHT = "#F7FAFA";

function Logo({ size = 32, dark = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill={dark ? WHITE : TEAL} />
        <path d="M12 26 L20 14 L28 26" stroke={dark ? TEAL : WHITE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="20" cy="14" r="3" fill={GOLD}/>
        <path d="M14 22 L26 22" stroke={dark ? TEAL : WHITE} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
      </svg>
      <span style={{
        fontFamily: "'Georgia', serif",
        fontSize: size * 0.85,
        fontWeight: 700,
        color: dark ? WHITE : TEAL,
        letterSpacing: "-0.5px"
      }}>
        match<span style={{ color: GOLD }}>kid</span>
      </span>
    </div>
  );
}

function StarRating({ n = 5 }) {
  return (
    <span style={{ color: GOLD, fontSize: 16, letterSpacing: 1 }}>
      {"★".repeat(n)}
    </span>
  );
}

const TEMOIGNAGES = [
  {
    text: "On avait inscrit notre fille à la gym, au tennis, à la natation. Trois abandons en deux ans. Matchkid nous a dit danse contemporaine en 15 minutes. Elle en est à sa troisième année.",
    author: "Sophie M.", role: "Maman de Chloé, 11 ans", stars: 5
  },
  {
    text: "Le profil de mon fils était tellement juste que j'ai relu le rapport trois fois. Les recommandations correspondaient exactement à ce que j'observais sans pouvoir mettre des mots dessus.",
    author: "Thomas R.", role: "Papa de Lucas, 9 ans", stars: 5
  },
  {
    text: "Ce qui m'a convaincue, c'est la section 'activités à éviter'. Ils avaient listé l'athlétisme, qu'on lui avait imposé l'année précédente. Il avait détesté ça.",
    author: "Isabelle D.", role: "Maman de Théo, 13 ans", stars: 5
  },
];

const ETAPES = [
  {
    num: "01",
    titre: "Choisissez la tranche d'âge",
    texte: "4 questionnaires adaptés : 5-7 ans (parents), 8-11 ans (enfant + parents), 12-14 ans et 15-17 ans (ado + parents).",
    icon: "👶"
  },
  {
    num: "02",
    titre: "Remplissez ensemble",
    texte: "15 à 20 minutes. L'enfant répond seul à ses questions. Les parents répondent séparément. Les deux regards s'enrichissent mutuellement.",
    icon: "✏️"
  },
  {
    num: "03",
    titre: "Téléchargez le rapport",
    texte: "Un rapport PDF personnalisé : profil, forces naturelles, top 3 activités, activités à éviter, conseils aux parents.",
    icon: "📄"
  },
];

const PROFILS = [
  { emoji: "⚡", nom: "L'Intensif", desc: "Il a besoin de se dépenser — natation, athlétisme, escalade" },
  { emoji: "🎯", nom: "Le Technicien", desc: "Il veut maîtriser — escrime, tir à l'arc, tennis" },
  { emoji: "🎨", nom: "L'Artiste-Expressif", desc: "Il crée et s'exprime — danse, musique, théâtre" },
  { emoji: "⚔️", nom: "Le Compétiteur", desc: "Il a besoin d'un enjeu — sports de combat, échecs" },
  { emoji: "🤝", nom: "Le Collectif", desc: "Il s'épanouit en groupe — foot, basket, chorale" },
  { emoji: "🎭", nom: "Le Performeur", desc: "Il brille devant le public — théâtre, danse de scène, cirque" },
];


function FAQSection() {
  const [open, setOpen] = useState(null);
  const items = [
    { q: "À quoi sert Matchkid ?", a: "Matchkid aide les parents à choisir l'activité sportive ou artistique la mieux adaptée au profil de leur enfant. En quelques minutes, le diagnostic identifie les forces, les besoins et les préférences de l'enfant pour recommander les activités qui lui correspondent vraiment." },
    { q: "À partir de quel âge puis-je utiliser Matchkid ?", a: "Matchkid est conçu pour les enfants de 5 à 17 ans, répartis en 4 tranches d'âge : 5-7 ans, 8-11 ans, 12-14 ans et 15-17 ans. Le questionnaire s'adapte automatiquement à l'âge de votre enfant." },
    { q: "Mon enfant doit-il répondre lui-même au questionnaire ?", a: "Cela dépend de son âge. Pour les 5-7 ans, c'est 100% les parents qui répondent. À partir de 8 ans, l'enfant répond à sa partie et les parents à la leur. C'est ce regard croisé qui rend le diagnostic fiable." },
    { q: "Combien de temps dure le questionnaire ?", a: "Entre 10 et 15 minutes selon l'âge de l'enfant." },
    { q: "Qu'est-ce que je reçois à la fin ?", a: "Un rapport PDF personnalisé de 3 pages, téléchargeable immédiatement. Il contient le profil dominant et secondaire de votre enfant parmi 7 types, le Top 3 des activités recommandées, les activités à explorer, les activités à éviter, et des conseils personnalisés aux parents." },
    { q: "Quels sont les 7 profils possibles ?", a: "L'Intensif, Le Technicien, L'Artiste-Expressif, Le Compétiteur, Le Collectif, Le Performeur et Le Créateur (pour les 5-7 ans). Chaque profil correspond à un mode de fonctionnement naturel de l'enfant." },
    { q: "Est-ce que Matchkid remplace l'avis d'un professionnel ?", a: "Non. Matchkid est un outil d'aide à la décision pratique, pas un test clinique. Il ne remplace pas l'avis d'un psychologue ou d'un médecin. Son objectif est d'aider les parents à mieux connaître leur enfant pour faire un choix éclairé." },
    { q: "Combien ça coûte ?", a: "14,90 € en paiement unique, sans abonnement. Vous payez une fois et recevez le rapport immédiatement." },
    { q: "Puis-je refaire le diagnostic si mon enfant change ?", a: "Oui, vous pouvez refaire un diagnostic à tout moment — par exemple si votre enfant change de tranche d'âge ou si ses centres d'intérêt évoluent." },
    { q: "Mes données sont-elles protégées ?", a: "Oui. Matchkid ne recueille aucune donnée (ni les réponses au questionnaire, ni les rapports générés). Aucune donnée personnelle n'est revendue à des tiers." },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((item, i) => (
        <div key={i} style={{
          background: WHITE, borderRadius: 16, overflow: "hidden",
          boxShadow: "0 2px 12px rgba(27,107,107,0.06)",
          border: open === i ? `1px solid ${TEAL}` : "1px solid transparent"
        }}>
          <button onClick={() => setOpen(open === i ? null : i)} style={{
            width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "20px 24px", background: "none", border: "none", cursor: "pointer",
            textAlign: "left", gap: 16
          }}>
            <span style={{ fontFamily: "system-ui", fontSize: "clamp(14px, 2vw, 16px)", fontWeight: 700, color: DARK, lineHeight: 1.4 }}>{item.q}</span>
            <span style={{
              fontSize: 22, color: TEAL, flexShrink: 0,
              transform: open === i ? "rotate(45deg)" : "rotate(0deg)",
              transition: "transform 0.2s"
            }}>+</span>
          </button>
          {open === i && (
            <div style={{ padding: "0 24px 20px" }}>
              <p style={{ fontFamily: "system-ui", fontSize: "clamp(13px, 2vw, 15px)", color: MID, lineHeight: 1.7, margin: 0 }}>{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function LandingPage({ onStart, onCGV }) {
  const [scrolled, setScrolled] = useState(false);
  const [temoignageIdx, setTemoignageIdx] = useState(0);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTemoignageIdx(i => (i + 1) % TEMOIGNAGES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ fontFamily: "'Georgia', serif", background: WHITE, color: DARK, minHeight: "100vh", overflowX: "hidden" }}>

      {/* NAVIGATION */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? "rgba(255,255,255,0.97)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        boxShadow: scrolled ? "0 1px 20px rgba(27,107,107,0.1)" : "none",
        transition: "all 0.3s",
        padding: "16px 5%",
        display: "flex", justifyContent: "space-between", alignItems: "center"
      }}>
        <Logo size={28} dark={!scrolled} />
        <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
          <a href="#comment" style={{ fontFamily: "system-ui", fontSize: 14, color: scrolled ? MID : "rgba(255,255,255,0.85)", textDecoration: "none", fontWeight: 500, display: window.innerWidth < 600 ? "none" : "inline" }}>Comment ça marche</a>
          <a href="#profils" style={{ fontFamily: "system-ui", fontSize: 14, color: scrolled ? MID : "rgba(255,255,255,0.85)", textDecoration: "none", fontWeight: 500, display: window.innerWidth < 600 ? "none" : "inline" }}>Les profils</a>
          <button onClick={onStart} style={{
            background: GOLD, color: DARK, border: "none", borderRadius: 100,
            padding: "10px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer",
            fontFamily: "system-ui", transition: "all 0.2s"
          }}>
            Commencer
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{
        background: `linear-gradient(160deg, ${DARK} 0%, ${TEAL} 60%, ${TEAL_MID} 100%)`,
        minHeight: "100vh", display: "flex", alignItems: "center",
        padding: "100px 4% 60px", position: "relative", overflow: "hidden"
      }}>
        {/* Cercles décoratifs */}
        <div style={{ position: "absolute", top: "-10%", right: "-5%", width: 500, height: 500, borderRadius: "50%", background: "rgba(232,200,122,0.06)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "-15%", left: "-8%", width: 600, height: 600, borderRadius: "50%", background: "rgba(255,255,255,0.03)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 800, position: "relative", zIndex: 1, margin: "0 auto", textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(232,200,122,0.15)", border: "1px solid rgba(232,200,122,0.3)",
            borderRadius: 100, padding: "6px 16px", marginBottom: 32
          }}>
            <span style={{ fontSize: 12, color: GOLD, fontFamily: "system-ui", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
              Nouveau — Diagnostic en 15 minutes
            </span>
          </div>

          <h1 style={{
            fontSize: "clamp(36px, 6vw, 68px)", fontWeight: 700, color: WHITE,
            lineHeight: 1.1, margin: "0 0 24px", letterSpacing: "-1px"
          }}>
            Trouvons l'activité<br />
            <span style={{ color: GOLD }}>qui lui ressemble</span><br />
            vraiment.
          </h1>

          <p style={{
            fontSize: "clamp(16px, 2vw, 20px)", color: "rgba(255,255,255,0.75)",
            lineHeight: 1.7, margin: "0 auto 40px", maxWidth: 560, fontFamily: "system-ui", fontWeight: 400
          }}>
            Vous avez inscrit votre enfant dans des activités qu'il a abandonnées ?<br />
            Matchkid identifie son profil naturel et recommande les activités<br />
            sportives et artistiques qui lui correspondent vraiment.
          </p>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
            <button onClick={onStart} style={{
              background: GOLD, color: DARK, border: "none", borderRadius: 100,
              padding: "18px 40px", fontSize: 18, fontWeight: 700, cursor: "pointer",
              fontFamily: "system-ui", boxShadow: "0 8px 32px rgba(232,200,122,0.4)",
              transition: "all 0.2s"
            }}>
              Découvrir le profil de mon enfant →
            </button>
            <span style={{ fontFamily: "system-ui", fontSize: 14, color: "rgba(255,255,255,0.5)" }}>
              14,90 € · Rapport PDF · Sans abonnement
            </span>
          </div>

          <div style={{ marginTop: 48, display: "flex", gap: 40, flexWrap: "wrap", justifyContent: "center" }}>
            {[
              { n: "4", label: "tranches d'âge" },
              { n: "6", label: "profils distincts" },
              { n: "30", label: "combinaisons possibles" },
              { n: "14,90 €", label: "sans abonnement" },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 28, fontWeight: 700, color: GOLD }}>{s.n}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", fontFamily: "system-ui", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LE PROBLÈME */}
      <section style={{ background: LIGHT, padding: "60px 5%" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: TEAL, textTransform: "uppercase", marginBottom: 16 }}>Le problème</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, color: DARK, lineHeight: 1.2, margin: "0 0 20px" }}>
              Vous avez déjà dépensé 350 €<br />dans une activité abandonnée.
            </h2>
            <p style={{ fontFamily: "system-ui", fontSize: 17, color: MID, lineHeight: 1.7, maxWidth: 600, margin: "0 auto" }}>
              En France, 60 % des enfants abandonnent leur activité dans les 12 premiers mois. 
              Non par manque de motivation — mais parce que l'activité ne correspondait pas à qui ils sont vraiment.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24 }}>
            {[
              { icon: "😔", titre: "L'enfant qui s'ennuie", texte: "Il pratique sans plaisir. L'activité ne correspond pas à son énergie naturelle." },
              { icon: "💸", titre: "L'argent gaspillé", texte: "350 € en moyenne par inscription. Deux ou trois essais avant de trouver quelque chose qui dure." },
              { icon: "🎯", titre: "Le choix au hasard", texte: "On inscrit parce que le copain y va, ou parce qu'on pratiquait soi-même. Pas parce que ça correspond à l'enfant." },
            ].map((c, i) => (
              <div key={i} style={{
                background: WHITE, borderRadius: 20, padding: "32px 28px",
                boxShadow: "0 4px 24px rgba(27,107,107,0.06)",
                borderTop: `3px solid ${TEAL}`
              }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>{c.icon}</div>
                <h3 style={{ fontFamily: "system-ui", fontSize: 17, fontWeight: 700, color: DARK, margin: "0 0 10px" }}>{c.titre}</h3>
                <p style={{ fontFamily: "system-ui", fontSize: 14, color: MID, lineHeight: 1.6, margin: 0 }}>{c.texte}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section id="comment" style={{ background: WHITE, padding: "60px 5%" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 72 }}>
            <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: TEAL, textTransform: "uppercase", marginBottom: 16 }}>Comment ça marche</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, color: DARK, lineHeight: 1.2, margin: 0 }}>
              15 minutes pour trouver la bonne direction
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 32 }}>
            {ETAPES.map((e, i) => (
              <div key={i} style={{ position: "relative" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: TEAL_PALE, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, marginBottom: 20
                }}>
                  {e.icon}
                </div>
                <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: 1, marginBottom: 8 }}>ÉTAPE {e.num}</div>
                <h3 style={{ fontFamily: "system-ui", fontSize: 18, fontWeight: 700, color: DARK, margin: "0 0 12px" }}>{e.titre}</h3>
                <p style={{ fontFamily: "system-ui", fontSize: 14, color: MID, lineHeight: 1.7, margin: 0 }}>{e.texte}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 64, background: TEAL_PALE, borderRadius: 24, padding: "40px 48px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24 }}>
            <div>
              <h3 style={{ fontFamily: "system-ui", fontSize: 20, fontWeight: 700, color: DARK, margin: "0 0 8px" }}>
                Le double regard — l'avantage clé
              </h3>
              <p style={{ fontFamily: "system-ui", fontSize: 15, color: MID, lineHeight: 1.6, margin: 0, maxWidth: 480 }}>
                Contrairement aux quiz qui ne consultent que les parents, Matchkid croise 
                systématiquement le regard de l'enfant ET des parents. Ce croisement produit 
                les recommandations les plus précises.
              </p>
            </div>
            <button onClick={onStart} style={{
              background: TEAL, color: WHITE, border: "none", borderRadius: 100,
              padding: "16px 32px", fontSize: 16, fontWeight: 700, cursor: "pointer",
              fontFamily: "system-ui", whiteSpace: "nowrap"
            }}>
              Commencer →
            </button>
          </div>
        </div>
      </section>

      {/* LES QUESTIONNAIRES PAR TRANCHE */}
      <section style={{ background: LIGHT, padding: "60px 5%" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: TEAL, textTransform: "uppercase", marginBottom: 16 }}>4 questionnaires adaptés</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, color: DARK, lineHeight: 1.2, margin: 0 }}>
              De 5 à 17 ans — toujours adapté
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 20 }}>
            {[
              { age: "5 — 7 ans", label: "Questionnaire A", sub: "100% parents", color: "#6B4FBB", bg: "#EDE8FF", desc: "À cet âge, ce sont les parents qui observent. Activités d'éveil recommandées." },
              { age: "8 — 11 ans", label: "Questionnaire B", sub: "Enfant + parents", color: TEAL, bg: TEAL_PALE, desc: "L'enfant répond à 8 scénarios illustrés. Les parents complètent avec 12 questions." },
              { age: "12 — 14 ans", label: "Questionnaire C", sub: "70% ado / 30% parents", color: "#1B5E20", bg: "#E8F5E9", desc: "L'ado est le meilleur expert de lui-même. Le parent nuance." },
              { age: "15 — 17 ans", label: "Questionnaire D", sub: "90% ado / 10% parents", color: "#4A2B8E", bg: "#EDE8FF", desc: "Quasi-autonomie. 18 questions pour l'ado, 3 pour les parents." },
            ].map((q, i) => (
              <div key={i} onClick={onStart} style={{
                background: WHITE, borderRadius: 20, padding: "28px 24px",
                cursor: "pointer", border: `2px solid ${q.bg}`,
                boxShadow: "0 4px 16px rgba(27,107,107,0.04)",
                transition: "all 0.2s"
              }}>
                <div style={{ fontFamily: "system-ui", fontSize: 11, fontWeight: 700, color: q.color, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{q.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: DARK, marginBottom: 4, fontFamily: "Georgia, serif" }}>{q.age}</div>
                <div style={{ fontFamily: "system-ui", fontSize: 12, color: q.color, background: q.bg, display: "inline-block", padding: "3px 10px", borderRadius: 100, marginBottom: 14, fontWeight: 600 }}>{q.sub}</div>
                <p style={{ fontFamily: "system-ui", fontSize: 13, color: MID, lineHeight: 1.6, margin: 0 }}>{q.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LES 6 PROFILS */}
      <section id="profils" style={{ background: WHITE, padding: "60px 5%" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: TEAL, textTransform: "uppercase", marginBottom: 16 }}>6 profils</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, color: DARK, lineHeight: 1.2, margin: "0 0 20px" }}>
              Quel est le profil de votre enfant ?
            </h2>
            <p style={{ fontFamily: "system-ui", fontSize: 17, color: MID, lineHeight: 1.7, maxWidth: 540, margin: "0 auto" }}>
              Matchkid identifie un profil principal et un profil secondaire — 30 combinaisons possibles pour une recommandation vraiment personnalisée.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
            {PROFILS.map((p, i) => (
              <div key={i} style={{
                background: LIGHT, borderRadius: 16, padding: "24px",
                display: "flex", gap: 16, alignItems: "flex-start"
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, background: TEAL_PALE,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, flexShrink: 0
                }}>{p.emoji}</div>
                <div>
                  <div style={{ fontFamily: "system-ui", fontSize: 15, fontWeight: 700, color: DARK, marginBottom: 4 }}>{p.nom}</div>
                  <div style={{ fontFamily: "system-ui", fontSize: 13, color: MID, lineHeight: 1.5 }}>{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section style={{ background: `linear-gradient(135deg, ${DARK}, ${TEAL})`, padding: "60px 5%" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: GOLD, textTransform: "uppercase", marginBottom: 48 }}>
            Ce que disent les parents
          </div>

          <div style={{ position: "relative", minHeight: 200 }}>
            {TEMOIGNAGES.map((t, i) => (
              <div key={i} style={{
                position: i === temoignageIdx ? "relative" : "absolute",
                opacity: i === temoignageIdx ? 1 : 0,
                transition: "opacity 0.5s",
                top: 0, left: 0, right: 0
              }}>
                <StarRating n={t.stars} />
                <blockquote style={{
                  fontSize: "clamp(18px, 2.5vw, 24px)", color: WHITE,
                  lineHeight: 1.6, margin: "24px 0", fontStyle: "italic",
                  fontWeight: 400
                }}>
                  "{t.text}"
                </blockquote>
                <div style={{ fontFamily: "system-ui", fontSize: 15, fontWeight: 700, color: GOLD }}>{t.author}</div>
                <div style={{ fontFamily: "system-ui", fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{t.role}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 40 }}>
            {TEMOIGNAGES.map((_, i) => (
              <button key={i} onClick={() => setTemoignageIdx(i)} style={{
                width: i === temoignageIdx ? 24 : 8, height: 8,
                borderRadius: 100, border: "none", cursor: "pointer",
                background: i === temoignageIdx ? GOLD : "rgba(255,255,255,0.3)",
                transition: "all 0.3s", padding: 0
              }} />
            ))}
          </div>
        </div>
      </section>

      {/* RAPPORT EXEMPLE */}
      <section style={{ background: LIGHT, padding: "60px 5%" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 40, alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: TEAL, textTransform: "uppercase", marginBottom: 16 }}>Le rapport</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, color: DARK, lineHeight: 1.2, margin: "0 0 24px" }}>
              Un rapport PDF élaboré, pas un quiz générique
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { icon: "🎯", titre: "Profil principal + secondaire", desc: "30 combinaisons possibles — chaque enfant est unique" },
                { icon: "📊", titre: "Les 4 dimensions du profil", desc: "Corps, Social, Mental, Moteur — une analyse complète" },
                { icon: "🏆", titre: "Top 3 activités recommandées", desc: "Avec l'explication de pourquoi ça correspond à cet enfant" },
                { icon: "⚠️", titre: "Activités à éviter", desc: "Aussi précieux que les recommandations positives" },
                { icon: "💡", titre: "5 conseils pour les parents", desc: "Pratiques, concrets, adaptés au profil spécifique" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: TEAL_PALE,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, flexShrink: 0
                  }}>{item.icon}</div>
                  <div>
                    <div style={{ fontFamily: "system-ui", fontSize: 15, fontWeight: 700, color: DARK }}>{item.titre}</div>
                    <div style={{ fontFamily: "system-ui", fontSize: 13, color: MID, lineHeight: 1.5 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aperçu rapport */}
          <div style={{
            background: WHITE, borderRadius: 24, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(27,107,107,0.12)",
            transform: "rotate(-1deg)"
          }}>
            <div style={{ background: TEAL, padding: "24px 28px" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontFamily: "system-ui", letterSpacing: 2, marginBottom: 8, textTransform: "uppercase" }}>Questionnaire Profil Enfant</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: WHITE }}>Louis</div>
              <div style={{ fontSize: 14, color: "#A0D4D4", fontFamily: "system-ui", marginBottom: 16 }}>8 ans · Garçon</div>
              <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: WHITE }}>🎨 L'Artiste-Expressif</div>
                <div style={{ fontSize: 11, color: "#A0D4D4", fontFamily: "system-ui" }}>Profil principal</div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "8px 14px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: WHITE }}>🎭 Le Performeur</div>
                <div style={{ fontSize: 11, color: "#A0D4D4", fontFamily: "system-ui" }}>Profil secondaire</div>
              </div>
            </div>
            <div style={{ padding: "20px 28px" }}>
              {[
                { dim: "CORPS", titre: "Expressif en mouvement", pct: 78 },
                { dim: "SOCIAL", titre: "Performeur naturel", pct: 65 },
                { dim: "MENTAL", titre: "Créatif avant règles", pct: 54 },
                { dim: "MOTEUR", titre: "Passion avant excellence", pct: 48 },
              ].map((d, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontFamily: "system-ui", fontSize: 11, fontWeight: 700, color: TEAL }}>{d.dim}</span>
                    <span style={{ fontFamily: "system-ui", fontSize: 11, color: MID }}>{d.titre}</span>
                  </div>
                  <div style={{ height: 6, background: "#F0F0F0", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: d.pct + "%", background: TEAL, borderRadius: 3 }} />
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 16, padding: "12px 16px", background: "#FDF6E3", borderRadius: 10, borderLeft: "3px solid #E8C87A" }}>
                <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, color: "#9B7060" }}>TOP 3 RECOMMANDATIONS</div>
                <div style={{ fontFamily: "system-ui", fontSize: 13, color: DARK, marginTop: 6 }}>
                  #1 Danse hip-hop · #2 Musique · #3 Théâtre
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section id="faq" style={{ background: LIGHT, padding: "60px 5%" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: TEAL, textTransform: "uppercase", marginBottom: 16 }}>FAQ</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, color: DARK, lineHeight: 1.2, margin: 0 }}>
              Questions fréquentes
            </h2>
          </div>
          <FAQSection />
        </div>
      </section>

      {/* CTA FINAL */}
      <section style={{
        background: `linear-gradient(135deg, ${TEAL}, ${DARK})`,
        padding: "60px 5%", textAlign: "center"
      }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <Logo size={40} dark />
          <h2 style={{
            fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 700, color: WHITE,
            lineHeight: 1.2, margin: "32px 0 20px"
          }}>
            Prêt à trouver l'activité<br />qui lui ressemble ?
          </h2>
          <p style={{ fontFamily: "system-ui", fontSize: 17, color: "rgba(255,255,255,0.7)", lineHeight: 1.7, margin: "0 0 40px" }}>
            15 minutes. Un rapport PDF complet. 14,90 €.<br />
            Moins cher qu'une inscription ratée.
          </p>
          <button onClick={onStart} style={{
            background: GOLD, color: DARK, border: "none", borderRadius: 100,
            padding: "20px 48px", fontSize: 20, fontWeight: 700, cursor: "pointer",
            fontFamily: "system-ui", boxShadow: "0 8px 32px rgba(232,200,122,0.4)"
          }}>
            Commencer le diagnostic →
          </button>
          <div style={{ fontFamily: "system-ui", fontSize: 13, color: "rgba(255,255,255,0.4)", marginTop: 20 }}>
            Rapport PDF téléchargeable immédiatement · Aucun abonnement
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: DARK, padding: "48px 5% 32px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 32, marginBottom: 40 }}>
            <div>
              <Logo size={24} dark />
              <p style={{ fontFamily: "system-ui", fontSize: 13, color: "rgba(255,255,255,0.4)", marginTop: 12, maxWidth: 260, lineHeight: 1.6 }}>
                Trouvons l'activité qui lui ressemble vraiment.
              </p>
            </div>
            <div style={{ display: "flex", gap: 60, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Service</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button onClick={onStart} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "system-ui", fontSize: 14, color: "rgba(255,255,255,0.7)", textAlign: "left", padding: 0 }}>Faire le diagnostic</button>
                  <a href="#comment" style={{ fontFamily: "system-ui", fontSize: 14, color: "rgba(255,255,255,0.7)", textDecoration: "none" }}>Comment ça marche</a>
                  <a href="#profils" style={{ fontFamily: "system-ui", fontSize: 14, color: "rgba(255,255,255,0.7)", textDecoration: "none" }}>Les 6 profils</a>
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "system-ui", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Légal</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button onClick={onCGV} style={{ fontFamily: "system-ui", fontSize: 14, color: "rgba(255,255,255,0.7)", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>CGV</button>
                  <a href="mailto:contact@matchkid.fr" style={{ fontFamily: "system-ui", fontSize: 14, color: "rgba(255,255,255,0.7)", textDecoration: "none" }}>Contact</a>
                </div>
              </div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <span style={{ fontFamily: "system-ui", fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
              © 2026 Matchkid
            </span>
            
          </div>
        </div>
      </footer>
    </div>
  );
}
